import { setAuthService } from '@/utils/http'
import axios from 'axios'
import qs from 'qs'
import { API_CONFIG } from '@/config/api'
import { configService, type CurrentUser } from '../config'
import { tokenService } from './token.service'
import type {
  LoginRequest,
  TokenResponse,
  UserProfile,
  AuthUser
} from './models'

/**
 * 认证服务类
 * 处理用户认证相关的功能
 */
export class AuthService {
  private readonly CLIENT_ID = 'Owl_App'
  private readonly CLIENT_SECRET = ''
  private readonly SCOPES = "Owl openid profile phone offline_access";
  private readonly TOKEN_ENDPOINT = `${API_CONFIG.baseURL}/connect/token`
  private readonly USER_INFO_ENDPOINT = `${API_CONFIG.baseURL}/connect/userinfo`

  /**
   * 用户登录
   * @param request 登录请求数据
   * @returns 认证用户信息
   */
  async login(request: LoginRequest): Promise<AuthUser> {
    try {
      const tokenData = (() => {
        if (request.method === 'password') {
          return qs.stringify({
            grant_type: 'password',
            client_id: this.CLIENT_ID,
            client_secret: this.CLIENT_SECRET,
            username: request.phoneNumber,
            password: request.password,
            scope: this.SCOPES
          })
        }

        return qs.stringify({
          grant_type: 'phone_code',
          client_id: this.CLIENT_ID,
          client_secret: this.CLIENT_SECRET,
          username: request.phoneNumber,
          code: request.code,
          scope: this.SCOPES
        })
      })()

      // 使用原始 axios 避免拦截器循环
      const response = await axios.post<TokenResponse>(this.TOKEN_ENDPOINT, tokenData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      })

      const tokenResponse = response.data

      console.log('[AuthService] Login successful, received token:', JSON.stringify(tokenResponse))



      const accessToken = tokenResponse.access_token || (tokenResponse as any).accessToken

      const expiresIn = tokenResponse.expires_in || (tokenResponse as any).expiresIn



      if (!accessToken) {

        throw new Error('Access token missing in response')

      }



      const expiresAt = Date.now() + (expiresIn * 1000)



      // 获取应用配置（包含当前用户信息和角色）

      let currentUser: CurrentUser | undefined

      try {

        const appConfig = await configService.getApplicationConfiguration(accessToken)

        // 验证用户是否已登录（id 和 userName 必须存在）

        if (appConfig.currentUser.id && appConfig.currentUser.userName) {

          currentUser = appConfig.currentUser

        }

      } catch (error) {

        console.warn('Failed to get application configuration:', error)

      }



      // 获取用户基本信息（从 /connect/userinfo）

      const userProfile = await this.getUserProfile(accessToken)



      const authUser: AuthUser = {

        access_token: accessToken,

        token_type: tokenResponse.token_type || (tokenResponse as any).tokenType,

        expires_at: expiresAt,

        refresh_token: tokenResponse.refresh_token || (tokenResponse as any).refreshToken,

        profile: userProfile,

        currentUser

      }

      // 存储完整用户信息
      tokenService.setTokens(authUser, request.rememberMe)

      return authUser
    } catch (error: any) {
      console.error('Login failed:', error)
      throw new Error(error.response?.data?.error_description || '登录失败')
    }
  }

  /**
   * 刷新Token
   * @returns 更新后的认证用户信息
   */
  async refreshToken(): Promise<AuthUser | null> {
    console.log('[AuthService] refreshToken() called')
    const refreshToken = tokenService.getRefreshToken()
    if (!refreshToken) {
      console.warn('[AuthService] No refresh token found in storage')
      return null
    }

    console.log('[AuthService] Refresh token found, attempting to refresh access token')

    try {
      const tokenData = qs.stringify({
        grant_type: 'refresh_token',
        client_id: this.CLIENT_ID,
        client_secret: this.CLIENT_SECRET,
        refresh_token: refreshToken
      })

      console.log('[AuthService] Sending refresh request to:', this.TOKEN_ENDPOINT)
      // 使用原始 axios 避免拦截器循环
      const response = await axios.post<TokenResponse>(this.TOKEN_ENDPOINT, tokenData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      })

      console.log('[AuthService] Refresh token request successful')
      const tokenResponse = response.data
      const expiresAt = Date.now() + (tokenResponse.expires_in * 1000)

      // 获取应用配置（包含当前用户信息和角色）
      let currentUser: CurrentUser | undefined
      try {
        const appConfig = await configService.getApplicationConfiguration(tokenResponse.access_token)
        // 验证用户是否已登录（id 和 userName 必须存在）
        if (appConfig.currentUser.id && appConfig.currentUser.userName) {
          currentUser = appConfig.currentUser
        }
      } catch (error) {
        console.warn('Failed to get application configuration during refresh:', error)
        // 如果获取失败，尝试从本地存储获取 (虽然现在都在 tokenService 实际上是一样的)
        // tokenService.getToken() 返回的是 AuthUser 结构，其中包含 currentUser
        const storedToken = tokenService.getToken()
        currentUser = storedToken?.profile as any // hacky, profile is userProfile not currentUser.
        // Actually, tokenService stores currentUser separately in USER_INFO_KEY.
        // We should probably rely on configService call. If it fails, maybe we just keep the old user info if we can't get new one?
        // But let's just proceed.
      }

      // 获取用户基本信息（从 /connect/userinfo）
      const userProfile = await this.getUserProfile(tokenResponse.access_token)

      const authUser: AuthUser = {
        access_token: tokenResponse.access_token,
        token_type: tokenResponse.token_type,
        expires_at: expiresAt,
        refresh_token: tokenResponse.refresh_token || refreshToken,
        profile: userProfile,
        currentUser
      }

      tokenService.setTokens(authUser, true)
      console.log('[AuthService] Token refresh completed successfully')
      return authUser
    } catch (error: any) {
      console.error('[AuthService] Token refresh failed:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      })
      this.logout()
      return null
    }
  }

  /**
   * 获取用户配置文件
   * @param accessToken 访问令牌
   * @returns 用户配置文件
   */
  private async getUserProfile(accessToken: string): Promise<UserProfile | undefined> {
    try {
      console.log('[AuthService] Getting user profile with token:', accessToken ? accessToken.substring(0, 10) + '...' : 'null')
      // 使用原始 axios 避免拦截器循环和自动重定向
      const response = await axios.get<UserProfile>(this.USER_INFO_ENDPOINT, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })
      return response.data
    } catch (error) {
      console.error('Failed to get user profile:', error)
      return undefined
    }
  }

  /**
   * 获取当前用户信息
   * @returns 认证用户信息
   */
  async getUser(): Promise<AuthUser | null> {
    // console.log('[AuthService] getUser() called') // Reduce logs
    const storedUser = tokenService.getToken() as AuthUser | null

    if (!storedUser) {
      // console.log('[AuthService] No stored user found, attempting token refresh')
      return await this.refreshToken()
    }

    // console.log('[AuthService] Found stored user', { expiresAt: storedUser.expires_at })

    if (storedUser.expires_at && (storedUser.expires_at - Date.now()) < 5 * 60 * 1000) {
      console.log('[AuthService] Token expiring soon, attempting refresh')
      const refreshedUser = await this.refreshToken()
      return refreshedUser || storedUser
    }

    return storedUser
  }

  /**
   * 刷新用户信息
   * @returns 当前用户信息
   */
  async refreshUserInfo(): Promise<CurrentUser | null> {
    try {
      const storedUser = await this.getUser()
      if (!storedUser || !storedUser.access_token) {
        return null
      }

      const appConfig = await configService.getApplicationConfiguration(storedUser.access_token)

      if (!appConfig.currentUser.id || !appConfig.currentUser.userName) {
        return null
      }

      storedUser.currentUser = appConfig.currentUser
      tokenService.setTokens(storedUser, true)

      return appConfig.currentUser
    } catch (error) {
      console.error('Failed to refresh user info:', error)
      return null
    }
  }

  /**
   * 检查是否已认证
   * @returns 是否已认证
   */
  async isAuthenticated(): Promise<boolean> {
    const user = await this.getUser()
    return user !== null && user.expires_at > Date.now()
  }

  /**
   * 获取访问Token
   * @returns 访问Token
   */
  async getAccessToken(): Promise<string | null> {
    return tokenService.getAccessToken()
  }

  /**
   * 用户登出
   */
  logout(): void {
    tokenService.clearTokens()
  }
}

// 创建单例实例
export const authService = new AuthService()

// 将实例注入到 http 模块，解决循环依赖
setAuthService(authService)